import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ArrowLeft,
  Phone,
  Mail,
  Building2,
  AlertCircle,
  Loader2,
  Sparkles,
} from "lucide-react";
import AbnLookup from "@/components/AbnLookup";
import PlatformHeader from "@/components/PlatformHeader";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const Step = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold ${
        done
          ? "bg-emerald-600 text-white"
          : active
          ? "bg-stone-900 text-white"
          : "bg-stone-200 text-stone-500"
      }`}
    >
      {done ? <Check className="w-4 h-4" /> : n}
    </div>
    <span className={`text-[12px] font-semibold ${active || done ? "text-stone-900" : "text-stone-400"}`}>
      {label}
    </span>
  </div>
);

const InputRow = ({ label, ...props }) => (
  <div>
    <label className="block text-[12px] font-semibold text-stone-700 mb-1.5">{label}</label>
    <input
      {...props}
      className={`w-full px-3 py-2.5 rounded-lg border text-[14px] focus:outline-none focus:border-stone-500 ${
        props.disabled ? "bg-stone-50 text-stone-600 border-stone-200" : "border-stone-300"
      } ${props.className || ""}`}
    />
  </div>
);

export default function ClaimBusinessPage() {
  // Mobile OTP
  const [mobile, setMobile] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpDemoCode, setOtpDemoCode] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [mobileVerified, setMobileVerified] = useState(false);

  // Email
  const [email, setEmail] = useState("");

  // ABN — resolved by central registry (auto-detects existing module links)
  const [abn, setAbn] = useState("");
  const [abnInfo, setAbnInfo] = useState(null); // { abn, entity_name, business_name, status, trading_names, modules, ... }
  const [businessNameOverride, setBusinessNameOverride] = useState("");

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const mobileLooksValid = /^04\d{8}$/.test(mobile.replace(/\D/g, ""));
  const stepDone = {
    mobile: mobileVerified,
    email: email.length > 4 && email.includes("@"),
    abn: !!abnInfo && abnInfo.status === "verified",
  };
  const allDone = stepDone.mobile && stepDone.email && stepDone.abn;

  const requestOtp = async () => {
    setOtpDemoCode(null);
    setOtpRequested(false);
    try {
      const r = await fetch(`${API}/api/directory/verify-mobile/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile }),
      });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setOtpRequested(true);
      setOtpDemoCode(j.demo_code || null);
    } catch (e) {
      alert(String(e));
    }
  };

  const verifyOtp = async () => {
    try {
      const r = await fetch(`${API}/api/directory/verify-mobile/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile, code: otpCode }),
      });
      if (!r.ok) throw new Error("Invalid code — try again");
      setMobileVerified(true);
    } catch (e) {
      alert(String(e));
    }
  };

  const handleAbnResolved = (info) => {
    setAbnInfo(info);
    if (info?.business_name) setBusinessNameOverride(info.business_name);
  };

  const submit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/directory/claim-business`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile,
          email,
          abn: abnInfo.abn,
          business_name_override: businessNameOverride,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Failed");

      // Register the cross-module link in the central ABN registry so
      // the next module knows this business is already on Directory.
      try {
        await fetch(`${API}/api/abn/link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            abn: abnInfo.abn,
            module: "directory",
            ref: j.claim?.business_id || j.claim?.id || abnInfo.abn,
            linked_by: email,
            meta: { business_name: businessNameOverride, claim_id: j.claim?.id },
          }),
        });
      } catch { /* non-fatal — link failure shouldn't break onboarding */ }

      setClaimResult(j.claim);
    } catch (e) {
      setSubmitError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (claimResult) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-stone-200 p-6 max-w-lg w-full text-center" data-testid="claim-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-serif text-2xl text-stone-900 mb-2">Claim submitted</h1>
          <p className="text-[14px] text-stone-600 mb-4">
            We&apos;ve verified <b>{claimResult.abr_business_name}</b> with the Australian Business Register.
            We&apos;ve sent an email verification link to <b>{claimResult.email}</b> and SMS confirmation to your mobile.
          </p>
          <div className="bg-stone-50 rounded-lg p-3 text-left text-[12px] text-stone-700 mb-4 space-y-1">
            <div className="flex justify-between"><span>Claim ID</span><span className="font-mono font-bold">{claimResult.claim_id}</span></div>
            <div className="flex justify-between"><span>Mobile</span><span><Check className="w-3 h-3 inline text-emerald-600" /> {claimResult.mobile}</span></div>
            <div className="flex justify-between"><span>Email</span><span>{claimResult.email} (pending verification)</span></div>
            <div className="flex justify-between"><span>ABN</span><span className="font-mono"><Check className="w-3 h-3 inline text-emerald-600" /> {claimResult.abn}</span></div>
            <div className="flex justify-between"><span>State / GST registered</span><span>{claimResult.abr_state} / {claimResult.abr_gst_registered ? "Yes" : "No"}</span></div>
          </div>
          <Link to="/directory" className="inline-block px-4 py-2 rounded-lg bg-stone-900 text-white text-[13px] font-semibold">
            Back to directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* ONE header for every module — see /app/memory/AGENT_RULES.md Rule #4.
          Focused signup flow: no primary nav, no cart, no book-CTA. */}
      <PlatformHeader
        maxWidth="max-w-3xl"
        brand={{ name: "Claim your business", to: "/directory" }}
        nav={[]}
        showBookCta={false}
        showCart={false}
        showAccount={false}
        extras={(
          <Link
            to="/directory"
            data-testid="claim-back-link"
            aria-label="Back to directory"
            className="w-9 h-9 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center text-stone-600 dark:text-stone-300"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
        )}
      />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Progress strip */}
        <div className="flex items-center justify-between bg-white rounded-2xl border border-stone-200 px-4 py-3">
          <Step n={1} label="Verify mobile" active={!stepDone.mobile} done={stepDone.mobile} />
          <div className="flex-1 h-px bg-stone-200 mx-2" />
          <Step n={2} label="Email" active={stepDone.mobile && !stepDone.email} done={stepDone.email} />
          <div className="flex-1 h-px bg-stone-200 mx-2" />
          <Step n={3} label="ABN" active={stepDone.email && !stepDone.abn} done={stepDone.abn} />
        </div>

        {/* Step 1: Mobile */}
        <section className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3" data-testid="step-mobile">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-stone-500" />
            <h2 className="font-serif text-lg text-stone-900">1. Verify mobile number</h2>
            {stepDone.mobile && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">VERIFIED</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
            <InputRow
              label="Australian mobile (04**)"
              type="tel"
              placeholder="0412 345 678"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              disabled={mobileVerified}
              data-testid="input-mobile"
            />
            <button
              onClick={requestOtp}
              disabled={!mobileLooksValid || mobileVerified}
              className="px-4 py-2.5 rounded-lg bg-stone-900 text-white text-[13px] font-semibold disabled:opacity-40"
              data-testid="request-otp"
            >
              {otpRequested ? "Resend code" : "Send code"}
            </button>
          </div>
          {otpRequested && !mobileVerified && (
            <>
              {otpDemoCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[12px] text-amber-800">
                  <b>DEMO MODE:</b> Twilio not wired yet. Your code is <span className="font-mono font-bold text-amber-900">{otpDemoCode}</span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
                <InputRow
                  label="Enter the 6-digit code we texted you"
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  data-testid="input-otp"
                />
                <button
                  onClick={verifyOtp}
                  disabled={otpCode.length < 4}
                  className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-[13px] font-semibold disabled:opacity-40"
                  data-testid="verify-otp"
                >
                  Verify
                </button>
              </div>
            </>
          )}
        </section>

        {/* Step 2: Email */}
        <section className={`bg-white rounded-2xl border border-stone-200 p-5 space-y-3 ${!stepDone.mobile ? "opacity-50 pointer-events-none" : ""}`} data-testid="step-email">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-stone-500" />
            <h2 className="font-serif text-lg text-stone-900">2. Business email</h2>
            {stepDone.email && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">CAPTURED</span>}
          </div>
          <InputRow
            label="We'll send a verification link to confirm"
            type="email"
            placeholder="hi@yourrestaurant.com.au"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="input-email"
          />
        </section>

        {/* Step 3: ABN — verified via the central registry (cross-module aware) */}
        <section className={`bg-white rounded-2xl border border-stone-200 p-5 space-y-3 ${!stepDone.email ? "opacity-50 pointer-events-none" : ""}`} data-testid="step-abn">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-stone-500" />
            <h2 className="font-serif text-lg text-stone-900">3. ABN verification</h2>
            {stepDone.abn && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">VERIFIED</span>}
          </div>
          <AbnLookup
            value={abn}
            onChange={setAbn}
            onResolve={handleAbnResolved}
            testId="claim-abn"
            label="11-digit ABN"
          />
          {abnInfo?.entity_name && (
            <div className="pt-2 border-t border-stone-200">
              <InputRow
                label="Business name to display (you can edit)"
                value={businessNameOverride}
                onChange={(e) => setBusinessNameOverride(e.target.value)}
                data-testid="input-business-name"
              />
            </div>
          )}
        </section>

        {submitError && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-[13px] text-rose-700">
            {submitError}
          </div>
        )}

        <button
          onClick={submit}
          disabled={!allDone || submitting}
          className="w-full px-4 py-3 rounded-xl bg-stone-900 text-white text-[14px] font-bold disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid="submit-claim"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Submit claim
        </button>
        <p className="text-[11px] text-stone-400 text-center">
          By submitting you agree to our terms. Mobile via Twilio SMS · Email verification link · ABN auto-checked against the Australian Business Register.
        </p>
      </div>
    </div>
  );
}
